import React from 'react';
import ClientSearch from '../ClientSearch';
import { bondOptions } from '../../../data/bondOptions';

const Label = ({ children }) => (
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
        {children}
    </label>
);

const inp = "w-full px-2 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition";

const Step1Inputs = ({
    formData,
    onChange,
    onSubmit,
    selectedClient,
    onClientSelect,
    onClientClear,
    onAddNewClient,
}) => {
    const selectedBond  = bondOptions.find(b => b.label === formData.bondType);
    const clientMissing = !selectedClient;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (clientMissing) return; // guard — button is also disabled, but belt-and-suspenders
        onSubmit(e);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">

            {/* ── Client (required) ─ Batch ──────────────────────────────── */}
            <div className="grid md:grid-cols-3 gap-2">
                <div className="col-span-2">
                    <Label>
                        Client <span className="text-red-400 normal-case font-normal">*</span>
                    </Label>
                    <ClientSearch
                        selectedClient={selectedClient}
                        onSelect={onClientSelect}
                        onClear={onClientClear}
                        onAddNew={onAddNewClient}
                    />
                    {clientMissing && (
                        <p className="text-xs text-gray-400 mt-1">Search or add a client to continue.</p>
                    )}
                </div>
                <div>
                    <Label>Batch No.</Label>
                    <input
                        type="text" name="batchNumber" value={formData.batchNumber}
                        onChange={onChange} required maxLength="10"
                        className={`${inp} tracking-widest`}
                    />
                </div>
            </div>

            {/* ── Grade — 3 separate fields ────────────────────────────── */}
            <div>
                <Label>Grade
                {/* Live grade preview */}
                {formData.gritCode && formData.bondType && formData.concentration !== '' && (
                    <p className="inline text-xs text-emerald-600 font-medium">
                        &nbsp;→&nbsp;
                        <span className="font-mono">{formData.gritCode}-{formData.bondType}-{formData.concentration}</span>
                        {selectedBond && (
                            <span className="text-gray-400 font-normal">
                                &nbsp;· {selectedBond.temperature}°C · Kryo {selectedBond.kryoPct}%
                            </span>
                        )}
                    </p>
                )}
                </Label>
                <div className="grid grid-cols-4 gap-2">
                    <div className="col-span-2">
                        <input
                            type="text" name="gritCode" value={formData.gritCode}
                            onChange={onChange} required placeholder="e.g. B151-49"
                            className={inp}
                        />
                        <p className="text-xs text-gray-400 mt-0.5">Grit</p>
                    </div>
                    <div className="col-span-1">
                        <select
                            name="bondType" value={formData.bondType}
                            onChange={onChange} required className={inp}
                        >
                            <option value="">Bond…</option>
                            {bondOptions.map(b => (
                                <option key={b.label} value={b.label}>{b.label}</option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-400 mt-0.5">Bond</p>
                    </div>
                    <div className="col-span-1">
                        <input
                            type="number" name="concentration" value={formData.concentration}
                            onChange={onChange} required step="any" min="0" placeholder="Conc."
                            className={inp}
                        />
                        <p className="text-xs text-gray-400 mt-0.5">Conc.</p>
                    </div>
                </div>
            </div>

            {/* ── Mould system / Size ──────────────────────────────────── */}
            <div className="grid grid-cols-5 gap-2">
                <div className="col-span-3">
                    <Label>Mould System <span className="normal-case font-normal text-gray-400">[mm]</span></Label>
                    <div className="flex items-center gap-1">
                        <input
                            type="number" name="mouldWidth" value={formData.mouldWidth}
                            onChange={onChange} required placeholder="Width" className={inp}
                        />
                        <span className="text-gray-400 font-bold flex-shrink-0">×</span>
                        <input
                            type="number" name="mouldLength" value={formData.mouldLength}
                            onChange={onChange} required placeholder="Length" className={inp}
                        />
                    </div>
                </div>
                <div className="col-span-2">
                    <Label>Size</Label>
                    <input
                        type="text" name="stoneSize" value={formData.stoneSize}
                        onChange={onChange} required placeholder="3x3x110" className={inp}
                    />
                </div>
            </div>

            {/* ── Size / Abrasive layer / Slot ─────────────────────────── */}
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <Label>Abrasive Layer <span className="normal-case font-normal text-gray-400">[mm]</span></Label>
                    <input
                        type="number" name="abrasiveLayer" value={formData.abrasiveLayer}
                        onChange={onChange} required step="any" className={inp}
                    />
                </div>
                <div>
                    <Label>Slot <span className="normal-case font-normal text-gray-400">[mm]</span></Label>
                    <input
                        type="number" name="slotWidth" value={formData.slotWidth}
                        onChange={onChange} required step="any" className={inp}
                    />
                </div>
            </div>

            {/* ── Counts ───────────────────────────────────────────────── */}
            <div className="grid grid-cols-3 gap-2">
                <div>
                    <Label>No. of Moulds</Label>
                    <input
                        type="number" name="numberOfMoulds" value={formData.numberOfMoulds}
                        onChange={onChange} required min="1" className={inp}
                    />
                </div>
                <div>
                    <Label>Sticks / Mould</Label>
                    <input
                        type="number" name="sticksPerMould" value={formData.sticksPerMould}
                        onChange={onChange} required min="1" className={inp}
                    />
                </div>
                <div>
                    <Label>Stones / Stick</Label>
                    <input
                        type="number" name="stonesPerStick" value={formData.stonesPerStick}
                        onChange={onChange} required min="1" className={inp}
                    />
                </div>
            </div>

            <button
                type="submit"
                disabled={clientMissing}
                className="w-full py-2.5 bg-gray-900 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg font-semibold text-sm tracking-wide transition-colors shadow-sm"
            >
                Generate Sheet →
            </button>
        </form>
    );
};

export default Step1Inputs;